import { Link } from '@tanstack/react-router'
import { Pencil } from 'lucide-react'
import { useId, useState } from 'react'
import { Card, CardHeader } from '@/components/ui/Card'
import { Modal } from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import { useIsAdmin } from '@/lib/auth'
import { brandRank } from '@/lib/brand'
import { toDecimal, toNumber } from '@/lib/decimal'
import { formatEuros } from '@/lib/format'
import { useUpdateClient } from '@/lib/clients-api'
import type {
  ClientDetail,
  ClientWriteInput,
  InvoiceGrouping,
  RateDisplay,
} from '@/lib/clients-types'
import { useBrandTemplates } from '@/lib/settings-api'
import { cn } from '@/lib/utils'
import { MONTH_NAMES } from './shared'

const GROUPING_LABEL: Record<InvoiceGrouping, string> = {
  BY_CATEGORY: 'Per categorie',
  BY_PERSON: 'Per persoon',
  BY_PROJECT: 'Per project',
  BY_BILLING_GROUP: 'Per factuurgroep (verouderd)',
  SINGLE_GROUP: 'Eén groep',
  BY_CATEGORY_LINES: 'Categorie als factuurregel',
}
const RATE_LABEL: Record<RateDisplay, string> = {
  PER_LINE: 'Per regel',
  ONCE: 'Eenmalig',
  HIDDEN: 'Verborgen',
}
const GROUPING_OPTIONS: Array<[InvoiceGrouping, string]> = [
  ['BY_CATEGORY', GROUPING_LABEL.BY_CATEGORY],
  ['BY_PERSON', GROUPING_LABEL.BY_PERSON],
  ['BY_PROJECT', GROUPING_LABEL.BY_PROJECT],
  ['BY_CATEGORY_LINES', GROUPING_LABEL.BY_CATEGORY_LINES],
  ['SINGLE_GROUP', GROUPING_LABEL.SINGLE_GROUP],
]
const RATE_OPTIONS = Object.entries(RATE_LABEL) as Array<[RateDisplay, string]>

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <span className="text-sm text-ink-faint">{label}</span>
      <span className="text-sm font-medium text-ink">{value}</span>
    </div>
  )
}

export function DetailsCard({ client }: { client: ClientDetail }) {
  const isAdmin = useIsAdmin()
  const [editOpen, setEditOpen] = useState(false)
  const updateClient = useUpdateClient(client.id)
  const archived = client.archived

  function toggleArchived() {
    updateClient.mutate({ archived: !archived })
  }

  return (
    <Card>
      <CardHeader
        title="Gegevens en weergave"
        action={
          isAdmin ? (
            <div className="flex items-center gap-1">
              <button type="button" className="btn-ghost h-8 px-2 text-sm" onClick={() => setEditOpen(true)}>
                <Pencil className="h-3.5 w-3.5" /> Bewerken
              </button>
              <button type="button" className="btn-ghost h-8 px-2 text-sm" onClick={toggleArchived} disabled={updateClient.isPending}>
                {archived ? 'Herstellen' : 'Archiveren'}
              </button>
            </div>
          ) : null
        }
      />
      <div className="mt-4 divide-y divide-hairline">
        <Field label="Adres" value={`${client.street}, ${client.postalCode} ${client.city}`} />
        {client.invoiceEmail ? <Field label="Factuur-e-mail" value={client.invoiceEmail} /> : null}
        <Field label="Betalingstermijn" value={`${client.paymentTermDays} dagen`} />
        <Field label="BTW" value={client.vatReversed ? 'Verlegd' : `${toNumber(client.vatPercentage)}%`} />
        <Field label="Groepering" value={GROUPING_LABEL[client.groupingStrategy]} />
        <Field label="Tariefweergave" value={RATE_LABEL[client.rateDisplay]} />
        <Field label="Totaaloverzicht" value={client.showTotalOverview ? 'Aan' : 'Uit'} />
        {client.groupingStrategy === 'BY_CATEGORY' ? (
          <Field label="Categorie-subtotalen" value={client.showCategorySubtotals ? 'Aan' : 'Uit'} />
        ) : null}
        <Field label="Factuur per project" value={client.separateInvoicePerProject ? 'Aan' : 'Uit'} />
      </div>
      <div className="mt-4 rounded-lg bg-fill-1 px-3 py-2.5">
        <p className="text-sm font-medium text-ink-soft">Reiskosten</p>
        <p className={cn('mt-1 text-sm', client.travelAgreement ? 'text-ink' : 'text-ink-faint')}>
          {client.travelAgreement ?? 'Geen afspraak vastgelegd'}
        </p>
        {client.lastTravelPerPerson.length > 0 ? (
          <div className="mt-2 divide-y divide-hairline">
            {client.lastTravelPerPerson.map((last) => (
              <div key={last.personName} className="flex items-baseline justify-between gap-4 py-1">
                <span className="text-sm text-ink-faint">
                  {last.personName} · {new Date(last.expenseDate).toLocaleDateString('nl-NL')}
                </span>
                <span className="money text-sm text-ink">{formatEuros(toNumber(last.amount))}</span>
              </div>
            ))}
          </div>
        ) : null}
        <p className="mt-1 text-xs text-ink-faint">
          De afspraak verschijnt bij ureninvoer; het laatste bedrag per persoon staat daar als voorstel.
        </p>
      </div>
      <div className="mt-4 rounded-lg bg-fill-1 px-3 py-2.5">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-ink-soft">Rapportage</p>
          <Link to="/rapportages" className="text-xs text-accent-700 hover:underline">Beheren →</Link>
        </div>
        <p className="mt-1 text-sm text-ink-faint">
          {client.projects.some((p) => (p.reportRecipients?.length ?? 0) > 0)
            ? 'Ontvangers per project ingesteld'
            : client.reportRecipients.length === 0
              ? 'Geen ontvangers ingesteld'
              : `${client.reportRecipients.length} ontvanger${client.reportRecipients.length === 1 ? '' : 's'} voor alle projecten`}
          {client.weeklyReportEnabled ? ' · wekelijkse verzending aan' : ''}
        </p>
        <p className="mt-1 text-xs text-ink-faint">Rapportage-instellingen beheer je op de pagina Rapportages.</p>
      </div>
      {editOpen ? <EditClientModal client={client} onClose={() => setEditOpen(false)} /> : null}
    </Card>
  )
}

function EditClientModal({ client, onClose }: { client: ClientDetail; onClose: () => void }) {
  const brandLabelId = useId()
  const groupingLabelId = useId()
  const rateLabelId = useId()
  const indexationLabelId = useId()
  const updateClient = useUpdateClient(client.id)
  const templates = useBrandTemplates()
  const brandOptions = [...(templates.data ?? [])]
    .sort((a, b) => brandRank(a.code) - brandRank(b.code))
    .map((template) => ({ value: template.code, label: template.displayName }))
  const [companyName, setCompanyName] = useState(client.companyName)
  const [brandCode, setBrandCode] = useState(client.brand.code)
  const [invoiceEmail, setInvoiceEmail] = useState(client.invoiceEmail ?? '')
  const [street, setStreet] = useState(client.street)
  const [postalCode, setPostalCode] = useState(client.postalCode)
  const [city, setCity] = useState(client.city)
  const [paymentTermDays, setPaymentTermDays] = useState(String(client.paymentTermDays))
  const [vatPercentage, setVatPercentage] = useState(String(toNumber(client.vatPercentage)).replace('.', ','))
  const [vatReversed, setVatReversed] = useState(client.vatReversed)
  const [groupingStrategy, setGroupingStrategy] = useState<InvoiceGrouping>(client.groupingStrategy)
  const [rateDisplay, setRateDisplay] = useState<RateDisplay>(client.rateDisplay)
  const [showTotalOverview, setShowTotalOverview] = useState(client.showTotalOverview)
  const [showCategorySubtotals, setShowCategorySubtotals] = useState(client.showCategorySubtotals)
  const [separateInvoicePerProject, setSeparateInvoicePerProject] = useState(client.separateInvoicePerProject)
  const [internal, setInternal] = useState(client.internal)
  const [indexationMonth, setIndexationMonth] = useState(client.indexationMonth ? String(client.indexationMonth) : 'none')
  const [travelAgreement, setTravelAgreement] = useState(client.travelAgreement ?? '')
  const [invoiceSubjectTemplate, setInvoiceSubjectTemplate] = useState(client.invoiceSubjectTemplate ?? '')
  const [invoiceLineGroupHeading, setInvoiceLineGroupHeading] = useState(client.invoiceLineGroupHeading ?? '')

  function save() {
    const input: ClientWriteInput = {
      companyName: companyName.trim(),
      brandCode,
      invoiceEmail: invoiceEmail.trim() || null,
      travelAgreement: travelAgreement.trim() || null,
      street: street.trim(),
      postalCode: postalCode.trim(),
      city: city.trim(),
      paymentTermDays: Number(paymentTermDays) || 0,
      vatPercentage: toDecimal(toNumber(vatPercentage.replace(',', '.'))),
      vatReversed,
      groupingStrategy,
      rateDisplay,
      showTotalOverview,
      separateInvoicePerProject,
      internal,
      invoiceSubjectTemplate: invoiceSubjectTemplate.trim() || null,
      invoiceLineGroupHeading: invoiceLineGroupHeading.trim() || null,
      indexationMonth: indexationMonth === 'none' ? null : Number(indexationMonth),
    }
    if (groupingStrategy === 'BY_CATEGORY') {
      input.showCategorySubtotals = showCategorySubtotals
    }
    updateClient.mutate(input, { onSuccess: onClose })
  }

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Klant bewerken"
      description="Gegevens en weergave-instellingen aanpassen."
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>Annuleren</button>
          <button type="button" className="btn-primary" onClick={save} disabled={!companyName.trim() || updateClient.isPending}>
            {updateClient.isPending ? 'Opslaan…' : 'Opslaan'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Bedrijfsnaam</span>
          <input value={companyName} onChange={(e) => setCompanyName(e.target.value)} className="field" />
        </label>
        <label className="block">
          <span id={brandLabelId} className="mb-1.5 block text-sm font-medium text-ink-soft">Huisstijl</span>
          <Select value={brandCode} onValueChange={setBrandCode} options={brandOptions} ariaLabelledby={brandLabelId} className="w-full" />
          <span className="mt-1 block text-xs text-ink-faint">
            Geldt vanaf de volgende factuur. Bestaande facturen houden de huisstijl waarmee ze zijn aangemaakt, ook concepten die nog openstaan.
          </span>
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Factuur-e-mail</span>
          <input value={invoiceEmail} onChange={(e) => setInvoiceEmail(e.target.value)} type="email" className="field" placeholder="facturen@klant.nl" />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Reiskosten-afspraak</span>
          <textarea
            value={travelAgreement}
            onChange={(e) => setTravelAgreement(e.target.value)}
            rows={2}
            className="field"
            placeholder="bv. 50 ct/km vanaf Ermelo, max EUR 150 per keer"
          />
          <span className="mt-1 block text-xs text-ink-faint">Wordt getoond bij het invoeren van reiskosten.</span>
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Adres</span>
          <input value={street} onChange={(e) => setStreet(e.target.value)} className="field" placeholder="Straat en nummer" />
        </label>
        <div className="grid grid-cols-[minmax(0,120px)_minmax(0,1fr)] gap-2">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-ink-soft">Postcode</span>
            <input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} className="field" />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-ink-soft">Plaats</span>
            <input value={city} onChange={(e) => setCity(e.target.value)} className="field" />
          </label>
        </div>
        <div className="grid grid-cols-2 items-end gap-2">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-ink-soft">Betalingstermijn (dagen)</span>
            <input value={paymentTermDays} onChange={(e) => setPaymentTermDays(e.target.value)} inputMode="numeric" className="field num text-right" />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-ink-soft">BTW %</span>
            <input value={vatPercentage} onChange={(e) => setVatPercentage(e.target.value)} inputMode="decimal" className="field num text-right" />
          </label>
        </div>
        <label className="flex cursor-pointer items-center gap-1.5 text-sm text-ink-soft">
          <input type="checkbox" checked={vatReversed} onChange={(e) => setVatReversed(e.target.checked)} className="h-3.5 w-3.5 accent-accent-600" />
          BTW verlegd
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span id={groupingLabelId} className="mb-1.5 block text-sm font-medium text-ink-soft">Groepering</span>
            <Select
              value={groupingStrategy}
              onValueChange={(v) => setGroupingStrategy(v as InvoiceGrouping)}
              options={(groupingStrategy === 'BY_BILLING_GROUP'
                ? [...GROUPING_OPTIONS, ['BY_BILLING_GROUP', GROUPING_LABEL.BY_BILLING_GROUP] as [InvoiceGrouping, string]]
                : GROUPING_OPTIONS
              ).map(([value, label]) => ({ value, label }))}
              ariaLabelledby={groupingLabelId}
              className="w-full"
            />
          </label>
          <label className="block">
            <span id={rateLabelId} className="mb-1.5 block text-sm font-medium text-ink-soft">Tariefweergave</span>
            <Select
              value={rateDisplay}
              onValueChange={(v) => setRateDisplay(v as RateDisplay)}
              options={RATE_OPTIONS.map(([value, label]) => ({ value, label }))}
              ariaLabelledby={rateLabelId}
              className="w-full"
            />
          </label>
        </div>
        <div className="space-y-2 rounded-lg bg-fill-1 px-3 py-2.5">
          <label className="flex cursor-pointer items-center gap-1.5 text-sm text-ink-soft">
            <input type="checkbox" checked={showTotalOverview} onChange={(e) => setShowTotalOverview(e.target.checked)} className="h-3.5 w-3.5 accent-accent-600" />
            Totaaloverzicht tonen
          </label>
          {groupingStrategy === 'BY_CATEGORY' ? (
            <label className="flex cursor-pointer items-center gap-1.5 text-sm text-ink-soft">
              <input type="checkbox" checked={showCategorySubtotals} onChange={(e) => setShowCategorySubtotals(e.target.checked)} className="h-3.5 w-3.5 accent-accent-600" />
              Categorie-subtotalen tonen
            </label>
          ) : null}
          <label className="flex cursor-pointer items-center gap-1.5 text-sm text-ink-soft">
            <input type="checkbox" checked={separateInvoicePerProject} onChange={(e) => setSeparateInvoicePerProject(e.target.checked)} className="h-3.5 w-3.5 accent-accent-600" />
            Aparte factuur per project
          </label>
          <label className="flex cursor-pointer items-center gap-1.5 text-sm text-ink-soft">
            <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} className="h-3.5 w-3.5 accent-accent-600" />
            Interne klant
          </label>
        </div>
        {internal ? (
          <p className="text-xs text-ink-faint">
            Een houder voor eigen werk, geen opdrachtgever. Uren hierop tellen niet mee als declarabel en leveren in de
            vooruitblik geen omzet op. De facturatie blijft ongewijzigd werken.
          </p>
        ) : null}
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-ink-soft">Factuuronderwerp</span>
            <input value={invoiceSubjectTemplate} onChange={(e) => setInvoiceSubjectTemplate(e.target.value)} className="field" placeholder="Consultancy diensten {maand}" />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-ink-soft">Groepskop op factuur</span>
            <input value={invoiceLineGroupHeading} onChange={(e) => setInvoiceLineGroupHeading(e.target.value)} className="field" placeholder="Consultancy" />
          </label>
          <span className="col-span-2 block text-xs text-ink-faint">Placeholders <code>{'{maand}'}</code> en <code>{'{jaar}'}</code>; leeg laten voor de standaardteksten.</span>
        </div>
        <label className="block">
          <span id={indexationLabelId} className="mb-1.5 block text-sm font-medium text-ink-soft">Indexatiemaand uurtarieven</span>
          <Select
            value={indexationMonth}
            onValueChange={setIndexationMonth}
            options={[
              { value: 'none', label: 'Geen indexatie-afspraak' },
              ...MONTH_NAMES.map((name, i) => ({ value: String(i + 1), label: name.charAt(0).toUpperCase() + name.slice(1) })),
            ]}
            ariaLabelledby={indexationLabelId}
            className="w-full"
          />
          <span className="mt-1 block text-xs text-ink-faint">Vanaf deze maand waarschuwt het systeem zolang de jaarlijkse tariefindexatie nog niet is toegepast.</span>
        </label>
        <p className="rounded-lg bg-fill-1 px-3 py-2 text-xs text-ink-faint">
          Rapportage-ontvangers en wekelijkse verzending beheer je op de pagina <Link to="/rapportages" className="text-accent-700 hover:underline">Rapportages</Link>.
        </p>
      </div>
    </Modal>
  )
}
